// Copyright Abridged, Inc. 2022,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {DeviceCredentialRenameRequest} from '../models/device-credential-rename-request.model.js';
import {DeviceCredential} from '../models/device-credential.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by WebauthnController.
 */
export interface WebauthnService {
  /**
   * @param userVerification
   * @param rpID
   * @param challenge
   * @param challengeFormat
   */
  generateAuthenticationOptions(
    userVerification: string | undefined,
    rpID: string | undefined,
    challenge: string | undefined,
    challengeFormat: 'jwt' | 'slug' | 'chain' | undefined,
  ): Promise<unknown>;

  /**
   * @param userVerification
   * @param rpID
   * @param challenge
   * @param challengeFormat
   */
  getAuthenticationOptions(
    userVerification: string | undefined,
    rpID: string | undefined,
    challenge: string | undefined,
    challengeFormat: 'jwt' | 'slug' | 'chain' | undefined,
  ): Promise<unknown>;

  /**
   * @param _requestBody
   * @returns OK
   */
  storeAuthenticationResponse(_requestBody: {}): Promise<string>;

  /**
   * @param _requestBody
   * @returns OK
   */
  verifyAuthenticationResponse(_requestBody: {}): Promise<{
    verified?: boolean;
    idToken?: string;
  }>;

  /**
   * @param id
   * @param _requestBody
   * @returns OK
   */
  renameCredential(
    id: string,
    _requestBody: DeviceCredentialRenameRequest,
  ): Promise<{}>;

  /**
   * @param id
   * @returns OK
   */
  getCredential(id: string): Promise<DeviceCredential>;

  /**
   * @param id
   * @returns OK
   */
  deleteCredential(id: string): Promise<{}>;

  /**
   * @param rpId
   * @returns Result set of DeviceCredential model instances
   */
  getCredentials(rpId?: string): Promise<{
    paginationToken?: string;
    items?: DeviceCredential[];
  }>;

  /**
   * @param userVerification
   * @param requireResidentKey
   * @param residentKey
   * @param rpID
   * @param challengeFormat
   */
  generateRegistrationOptions(
    userVerification: string | undefined,
    requireResidentKey: string | undefined,
    residentKey: string | undefined,
    rpID: string | undefined,
    challengeFormat: 'jwt' | 'slug' | 'chain' | undefined,
  ): Promise<unknown>;

  /**
   * @param userVerification
   * @param requireResidentKey
   * @param residentKey
   * @param rpID
   * @param challengeFormat
   */
  getRegistrationOptions(
    userVerification: string | undefined,
    requireResidentKey: string | undefined,
    residentKey: string | undefined,
    rpID: string | undefined,
    challengeFormat: 'jwt' | 'slug' | 'chain' | undefined,
  ): Promise<unknown>;

  /**
   * @param _requestBody
   * @returns OK
   */
  verifyRegistrationResponse(_requestBody: {}): Promise<{
    verified?: boolean;
  }>;
}

export class WebauthnServiceProvider implements Provider<WebauthnService> {
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<WebauthnService> {
    const service = await getService<{
      apis: {WebauthnController: WebauthnService};
    }>(this.dataSource);
    return service.apis['WebauthnController'];
  }
}
